// Synthetic songs with exact ground truth, for tests and the bench.
//
// A fixture has audio, the true beat times, and the true count of each beat.
// Tempo drift is a sine of the beat interval: drift 0.01 means ±1%.
// Nothing here is a recording; it exists so the tracker can be measured
// against truth. Real songs are measured with taps (tools/bench).

import { CLAVE, PHRASE_BEATS, type ClaveDirection } from './patterns.js';
import { bellStrokes, DEFAULT_BELL, BELL_VOICES, type BellVoice } from './bell.js';
import { congaStrokes, CONGA, type CongaStroke } from './conga.js';
import { bassLine, bassNote, BASS } from './bass.js';

export interface Fixture {
  readonly sampleRate: number;
  readonly samples: Float32Array;
  /** True beat times, seconds, ascending. */
  readonly beats: readonly number[];
  /** True count (1..8) of each beat. */
  readonly counts: readonly number[];
}

export interface FixtureOptions {
  readonly bpm: number;
  readonly beats: number;
  /** Amplitude of the sinusoidal tempo drift, as a fraction. 0 for none. */
  readonly drift?: number;
  /**
   * Tempo in BPM as a function of the beat index k and the time t in seconds since the first
   * beat. When given, it replaces `bpm` and `drift`: the interval after beat k is 60 / bpmAt(k, t_k).
   * For ramps (see `rampTempo`) and steps (`stepTempo`) that a band's speeding up would make.
   */
  readonly bpmAt?: (k: number, t: number) => number;
  readonly sampleRate?: number;
}

const DRIFT_CYCLE_BEATS = 64;
const LEAD_IN = 0.5;

/** Beat times with tempo drift, or with a tempo profile `bpmAt`. Beat k is at LEAD_IN + sum of intervals before it. */
export function beatSchedule(bpm: number, beats: number, drift = 0, bpmAt?: (k: number, t: number) => number): number[] {
  const times = [LEAD_IN];
  for (let k = 0; k < beats; k++) {
    const rate = bpmAt ? bpmAt(k, times[k]! - LEAD_IN) : bpm * (1 + drift * Math.sin((2 * Math.PI * k) / DRIFT_CYCLE_BEATS));
    times.push(times[k]! + 60 / rate);
  }
  return times.slice(0, beats);
}

/** Tempo that changes linearly in time from `from` to `to` BPM over `seconds`, then holds. */
export const rampTempo = (from: number, to: number, seconds: number) => (_k: number, t: number): number =>
  from + (to - from) * Math.min(1, Math.max(0, t / seconds));

/** Tempo `from` BPM up to beat `atBeat`, then `from * ratio`. */
export const stepTempo = (from: number, ratio: number, atBeat: number) => (k: number): number => (k < atBeat ? from : from * ratio);

/** Time of a fractional beat index (0 is beats[0]); the half-count positions use this. */
function timeAt(beats: readonly number[], x: number): number {
  const i = Math.floor(x);
  const frac = x - i;
  const next = beats[i + 1] ?? beats[i]! + (beats[i]! - beats[i - 1]!);
  return beats[i]! + frac * (next - beats[i]!);
}

/** Add a decaying tone or noise burst into the buffer at time t. With `wrap`, what runs past the end continues at the start (a loop). */
function burst(out: Float32Array, sr: number, t: number, seconds: number, gain: number, sound: (τ: number, j: number) => number, wrap = false): void {
  const i0 = Math.round(t * sr);
  const len = Math.round(seconds * sr);
  for (let j = 0; j < len && (wrap || i0 + j < out.length); j++) {
    const tau = j / sr;
    out[(i0 + j) % out.length]! += gain * Math.exp(-tau / (seconds / 4)) * sound(tau, j);
  }
}

const tone = (hz: number) => (τ: number) => Math.sin(2 * Math.PI * hz * τ);

/** Deterministic noise (no Math.random, so fixtures are reproducible). */
function noise(seed: number): (j: number) => number {
  return (j) => {
    let x = (j + seed) * 2654435761;
    x = Math.imul(x ^ (x >>> 15), 0x85ebca6b);
    return ((x ^ (x >>> 13)) & 0xffff) / 32768 - 1;
  };
}

/** A click on every beat, no accent. Counts are unknowable from it, so it tests the beat grid only. */
export function clickTrack(opts: FixtureOptions): Fixture {
  const sr = opts.sampleRate ?? 22050;
  const beats = beatSchedule(opts.bpm, opts.beats, opts.drift ?? 0, opts.bpmAt);
  const length = Math.ceil((beats.at(-1)! + 1) * sr);
  const samples = new Float32Array(length);
  for (const t of beats) burst(samples, sr, t, 0.02, 0.6, (τ) => tone(1000)(τ));
  return { sampleRate: sr, samples, beats, counts: beats.map((_, i) => (i % PHRASE_BEATS) + 1) };
}

/**
 * A salsa-like pattern: bell on 1 3 5 7, bass on 2& and 4, conga on 2, 4 and 4&,
 * son clave 3-2 (from patterns.ts). Off-beat energy is deliberate: a tracker that
 * follows the loudest onsets will drift to the off-beat.
 */
export function salsaPattern(opts: FixtureOptions): Fixture {
  const sr = opts.sampleRate ?? 22050;
  const beats = beatSchedule(opts.bpm, opts.beats, opts.drift ?? 0, opts.bpmAt);
  const length = Math.ceil((beats.at(-1)! + 1) * sr);
  const samples = new Float32Array(length);

  const bell = [1, 3, 5, 7];
  const bass = [2.5, 4];
  const conga = [2, 4, 4.5];
  const clave = CLAVE['3-2'];
  const rnd = noise(7);
  for (let phrase = 0; phrase * PHRASE_BEATS < beats.length; phrase++) {
    const base = phrase * PHRASE_BEATS;
    const at = (c: number) => (base + c - 1 < beats.length ? timeAt(beats, base + c - 1) : Infinity);
    for (const c of bell) if (at(c) < Infinity) burst(samples, sr, at(c), 0.12, 0.5, tone(1800));
    for (const c of bass) if (at(c) < Infinity) burst(samples, sr, at(c), 0.22, 0.8, tone(60));
    for (const c of conga) if (at(c) < Infinity) burst(samples, sr, at(c), 0.08, 0.35, (_, j) => rnd(j + phrase * 97));
    for (const c of clave) if (at(c) < Infinity) burst(samples, sr, at(c), 0.03, 0.25, tone(2500));
  }
  return {
    sampleRate: sr,
    samples,
    beats,
    counts: beats.map((_, i) => (i % PHRASE_BEATS) + 1),
  };
}

/**
 * Silence the audio from beat `from` for `beats` beats (a break; the true beats and counts are
 * unchanged). With `offBeatHits`, a low drum still plays on every half-beat through the break
 * (the band stops, the percussion carries on off the beat), which pulls a tracker off the beat.
 */
export function withBreak(fx: Fixture, from: number, beats: number, opts: { readonly offBeatHits?: boolean } = {}): Fixture {
  const samples = Float32Array.from(fx.samples);
  const a = Math.max(0, Math.round((fx.beats[from]! - 0.01) * fx.sampleRate));
  const b = Math.min(samples.length, Math.round((fx.beats[Math.min(from + beats, fx.beats.length - 1)]! - 0.01) * fx.sampleRate));
  samples.fill(0, a, b);
  if (opts.offBeatHits) {
    for (let k = from; k < Math.min(from + beats, fx.beats.length - 1); k++) {
      burst(samples, fx.sampleRate, timeAt(fx.beats, k + 0.5), 0.1, 0.9, tone(90));
    }
  }
  return { ...fx, samples };
}

/**
 * A pattern whose off-beats are louder than its on-beats: a soft click on every
 * beat, a loud bass-and-conga hit on every half-beat (the "&"). An onset-only tracker
 * can lock half a beat off. Truth is the on-beat grid; counts are per on-beat.
 */
export function offBeatAccent(opts: FixtureOptions): Fixture {
  const sr = opts.sampleRate ?? 22050;
  const beats = beatSchedule(opts.bpm, opts.beats, opts.drift ?? 0, opts.bpmAt);
  const length = Math.ceil((beats.at(-1)! + 1) * sr);
  const samples = new Float32Array(length);
  const rnd = noise(11);
  for (let k = 0; k < beats.length - 1; k++) {
    burst(samples, sr, beats[k]!, 0.03, 0.25, tone(1200));
    const half = timeAt(beats, k + 0.5);
    burst(samples, sr, half, 0.18, 0.9, tone(70));
    burst(samples, sr, half, 0.06, 0.5, (_, j) => rnd(j + k * 31));
  }
  return { sampleRate: sr, samples, beats, counts: beats.map((_, i) => (i % PHRASE_BEATS) + 1) };
}

/**
 * A salsa pattern whose arrangement changes for a stretch (S6c): from beat `from` for `beats` beats the band plays the `offBeatAccent` pattern instead
 * (a soft click on each beat, a loud bass and conga hit on each "&"), then returns. The beats and counts are those of `salsaPattern` throughout. It is the
 * failure of the S6b tracker on whole songs in miniature: for those beats the strongest onsets are all on the "&", and a tracker that follows the loudest
 * onsets slides off the beat, or gains or loses a whole beat, and keeps the wrong count for the rest of the song.
 */
export function offBeatSection(opts: FixtureOptions, from: number, beats: number): Fixture {
  const base = salsaPattern(opts), accent = offBeatAccent(opts);
  const samples = Float32Array.from(base.samples);
  const i0 = Math.round(base.beats[from]! * base.sampleRate);
  const i1 = Math.min(samples.length, Math.round(base.beats[Math.min(from + beats, base.beats.length - 1)]! * base.sampleRate));
  for (let i = i0; i < i1; i++) samples[i] = accent.samples[i] ?? 0;
  return { ...base, samples };
}

// ---- Rhythm Lab: a synthesized band with exact timing, for practising to --------------------------------

/** The four parts of the lab band. */
export type LabInstrument = 'clave' | 'conga' | 'bass' | 'bell';

/**
 * The fixture bell: the bell the lab had before the salsa cowbell (bell.ts), three sine tones in a 35 ms burst. The tracker and evidence
 * tests render their lab loops with it (`bell: FIXTURE_BELL`), so their audio is exactly what it was. Never use it in the app.
 */
export const FIXTURE_BELL = 'fixture';

/** What `labLoop` plays as its bell: one of the bell voices (bell.ts), or the fixture bell. */
export type LabBell = BellVoice | typeof FIXTURE_BELL;

/**
 * The fixture conga: the conga the lab had before the modelled drum (conga.ts), a slap of band-passed noise and a pitched sine burst for the open tones, on 2 4 4& 6 8 8&
 * only. The tracker and evidence tests render their lab loops with it (`conga: FIXTURE_CONGA`), so their audio is exactly what it was. Never use it in the app.
 */
export const FIXTURE_CONGA = 'fixture';

/** What `labLoop` plays as its conga: the modelled drum (conga.ts), or the fixture conga. */
export type LabConga = 'conga' | typeof FIXTURE_CONGA;

/**
 * The fixture bass: the bass the lab had before the modelled baby bass (bass.ts), three pitched sine bursts on 2& 4 6& 8 (E2 on the whole counts, A2 on the "&"). The tracker
 * and evidence tests render their lab loops with it (`bass: FIXTURE_BASS`), so their audio is exactly what it was. Never use it in the app.
 */
export const FIXTURE_BASS = 'fixture';

/** What `labLoop` plays as its bass: the modelled baby bass (bass.ts), or the fixture bass. */
export type LabBass = 'bass' | typeof FIXTURE_BASS;

export interface LabOptions {
  /** Tempo in beats per minute, 140 to 220. */
  readonly bpm: number;
  /** Length of the loop in phrases (8-counts), a whole number of at least 1. */
  readonly phrases: number;
  /** Son clave direction. Default 3-2. */
  readonly clave?: ClaveDirection;
  /** Which parts play. Default: all four. A part not named plays. */
  readonly on?: Readonly<Partial<Record<LabInstrument, boolean>>>;
  /** Loudness of each part, 0 to 1 before the mix is scaled to avoid clipping. Defaults: clave 0.5, bass the modelled bass's own (bass.ts; 0.7 for the fixture bass), conga the drum's own (conga.ts; 0.45 for the fixture conga), bell the voice's own (bell.ts; 0.35 for the fixture bell). */
  readonly levels?: Readonly<Partial<Record<LabInstrument, number>>>;
  /** The bell: `bongo` (default), `mambo` or `chacha` (the salsa cowbell, bell.ts), or `FIXTURE_BELL` for the old bell the tests keep. Timing is the same for all. */
  readonly bell?: LabBell;
  /** The conga: `conga` (default; the modelled drum with slap, open, heel and toe strokes, conga.ts), or `FIXTURE_CONGA` for the old conga the tests keep. Timing is the same for both. */
  readonly conga?: LabConga;
  /** The bass: `bass` (default; the modelled baby bass playing the anticipated bass line, bass.ts), or `FIXTURE_BASS` for the old bass the tests keep. Timing is the same for both. */
  readonly bass?: LabBass;
  /** Default 44100. */
  readonly sampleRate?: number;
}

/** A lab loop: the fixture (samples, exact beat times, counts) and the loop's exact length. */
export interface LabLoop extends Fixture {
  /** Exactly phrases * 8 * 60 / bpm seconds. Loop the buffer at this length, not at samples.length / sampleRate, which is rounded to a whole sample. */
  readonly loopSeconds: number;
}

/**
 * Where each part plays, in counts (2.5 is "2&"): ARCHITECTURE.md / DESIGN.md Rhythm Lab. Clave comes from patterns.ts.
 * `bell` is the bell's open stroke; `bellNeck` its light neck strokes in between (the fixture bell plays only `bell`). The conga's `congaSlap` and `congaOpen`
 * are all the fixture conga plays; the modelled drum adds its soft `congaHeel` and `congaToe` strokes in between, so it sounds on every half-beat.
 */
export const LAB_PARTS = {
  bell: [1, 3, 5, 7],
  bellNeck: [2, 2.5, 4, 4.5, 6, 6.5, 8, 8.5],
  bass: [2.5, 4, 6.5, 8],
  congaSlap: [2, 6],
  congaOpen: [4, 4.5, 8, 8.5],
  congaHeel: [1, 3, 5, 7],
  congaToe: [1.5, 2.5, 3.5, 5.5, 6.5, 7.5],
} as const;

/** The modelled conga's strokes and where each plays (conga.ts). */
const CONGA_PLAN: readonly (readonly [CongaStroke, readonly number[]])[] = [
  ['heel', LAB_PARTS.congaHeel], ['toe', LAB_PARTS.congaToe], ['slap', LAB_PARTS.congaSlap], ['open', LAB_PARTS.congaOpen],
];

const LAB_LEVELS: Readonly<Record<LabInstrument, number>> = { bass: 0.7, clave: 0.5, conga: 0.45, bell: 0.35 };

const attack = (f: (τ: number, j: number) => number) => (τ: number, j: number): number => Math.min(1, τ / 0.002) * f(τ, j);

/** Add a rendered stroke into the loop at sample `i0`, with what runs past the end wrapped to the start. */
function addStroke(out: Float32Array, i0: number, stroke: Float32Array, gain: number): void {
  const n = out.length;
  let i = i0 % n;
  for (let j = 0; j < stroke.length;) {
    const run = Math.min(stroke.length - j, n - i);
    for (let k = 0; k < run; k++) out[i + k]! += gain * stroke[j + k]!;
    j += run;
    i = 0;
  }
}

/**
 * A burst's two factors, rendered once: `burst` adds `gain * Math.exp(...) * sound(τ, j)` at each sample, and every phrase repeats the same bursts, so the
 * lab computes the exponential and the sound once per kind and multiplies the same three numbers in the same order for each hit: the loop is bit-identical
 * to rendering every burst afresh (the bench and tracker fixtures depend on that), at a fraction of the cost.
 */
function unit(sr: number, seconds: number, sound: (τ: number, j: number) => number): { readonly env: Float64Array; readonly snd: Float64Array } {
  const len = Math.round(seconds * sr);
  const env = new Float64Array(len), snd = new Float64Array(len);
  for (let j = 0; j < len; j++) {
    const tau = j / sr;
    env[j] = Math.exp(-tau / (seconds / 4));
    snd[j] = sound(tau, j);
  }
  return { env, snd };
}

/** Add a `unit` into the loop at time t, wrapping at the end, as `burst(..., true)` does. */
function place(out: Float32Array, sr: number, t: number, gain: number, u: { readonly env: Float64Array; readonly snd: Float64Array }): void {
  const i0 = Math.round(t * sr);
  for (let j = 0; j < u.env.length; j++) out[(i0 + j) % out.length]! += gain * u.env[j]! * u.snd[j]!;
}

/**
 * The lab loop: bell (open stroke on 1 3 5 7, light neck strokes on 2 2& 4 4& 6 6& 8 8&; see bell.ts), bass on 2& 4 6& 8 (a baby bass: the fifth of the chord on the "&" and the root of the next chord, early, on 4 and 8; see bass.ts),
 * conga (heel on 1 3 5 7, toe on the "&" of 1 2 3 5 6 7, slap on 2 and 6, open tones on 4 4& 8 8&; see conga.ts), son clave from patterns.ts. The first beat is at 0 and the buffer is exactly `phrases` long, with every
 * sound that rings past the end wrapped to the start, so it loops without a click. Tempo is constant, so the
 * beat times are exact, whatever the bell. Sounds: the bass's harmonic partials with a thump and a finger click (bass.ts), a bright short burst (clave), the cowbell's inharmonic partials with a stick click (bell.ts)
 * and the conga's skin partials with hand noise (conga.ts). The timing of every part repeats every 4 beats except the clave (the bass's pitches follow a 16-count progression,
 * but its notes fall on the same counts and carry the same energy), so with the clave off the loop carries no clave direction.
 */
export function labLoop(opts: LabOptions): LabLoop {
  const { bpm, phrases, clave = '3-2' } = opts;
  if (!(bpm >= 140 && bpm <= 220)) throw new RangeError('the lab plays 140 to 220 BPM');
  if (!Number.isInteger(phrases) || phrases < 1) throw new RangeError('the lab loop is a whole number of phrases');
  const sr = opts.sampleRate ?? 44100;
  const on = (i: LabInstrument) => opts.on?.[i] ?? true;
  const bell: LabBell = opts.bell ?? DEFAULT_BELL;
  const conga: LabConga = opts.conga ?? 'conga';
  const bass: LabBass = opts.bass ?? 'bass';
  const level = (i: LabInstrument) =>
    opts.levels?.[i] ??
    (i === 'bell' && bell !== FIXTURE_BELL ? BELL_VOICES[bell].level : i === 'conga' && conga !== FIXTURE_CONGA ? CONGA.level : i === 'bass' && bass !== FIXTURE_BASS ? BASS.level : LAB_LEVELS[i]);

  const count = phrases * PHRASE_BEATS;
  const period = 60 / bpm;
  const beats = Array.from({ length: count }, (_, k) => k * period);
  const samples = new Float32Array(Math.round(count * period * sr));
  const rnd = noise(23);
  const strokes = on('bell') && bell !== FIXTURE_BELL ? bellStrokes(bell, sr) : null; // rendered once, then placed
  const fixtureBell = on('bell') && bell === FIXTURE_BELL ? unit(sr, 0.14, attack((τ) => tone(760)(τ) + 0.6 * tone(1140)(τ) + 0.3 * tone(2280)(τ))) : null;
  const claveHit = on('clave') ? unit(sr, 0.06, attack((τ) => tone(2450)(τ) + 0.45 * tone(3600)(τ))) : null;
  const bassHit = (hz: number) => unit(sr, 0.3, attack((τ) => tone(hz)(τ) + 0.4 * tone(2 * hz)(τ) + 0.15 * tone(3 * hz)(τ)));
  const fixtureBass = on('bass') && bass === FIXTURE_BASS ? { beat: bassHit(82.4), and: bassHit(110) } : null; // E2 on the beat, A2 on the "&"
  const bassNotes = on('bass') && bass !== FIXTURE_BASS ? bassLine(phrases, LAB_PARTS.bass) : null; // the notes of the line; each is rendered once for its pitch and length, then placed
  const bassRendered = new Map<string, Float32Array>();
  const bassAt = (i: number): Float32Array => {
    // a note is stopped when the next one starts (the loop's first note follows its last), or after BASS.maxSeconds
    const note = bassNotes![i]!, next = bassNotes![(i + 1) % bassNotes!.length]!;
    const gap = (next.beat - note.beat + (i + 1 === bassNotes!.length ? count : 0)) * period;
    const seconds = Math.min(gap, BASS.maxSeconds);
    const key = `${note.midi} ${Math.round(seconds * sr)}`;
    let rendered = bassRendered.get(key);
    if (!rendered) bassRendered.set(key, (rendered = bassNote(note.hz, sr, seconds)));
    return rendered;
  };
  const congaTone = (hz: number) => {
    // the pitch falls a little in the first 20 ms, as a struck head does
    const phase = (τ: number) => 2 * Math.PI * hz * (τ + 0.15 * 0.02 * (1 - Math.exp(-τ / 0.02)));
    return unit(sr, 0.2, attack((τ) => Math.sin(phase(τ)) + 0.25 * Math.sin(2 * phase(τ))));
  };
  const congaOn = on('conga') && conga === FIXTURE_CONGA ? { beat: congaTone(230), and: congaTone(310) } : null; // the "&" tone is higher
  const congaDrum = on('conga') && conga !== FIXTURE_CONGA ? congaStrokes(sr) : null; // rendered once, then placed

  if (bassNotes) bassNotes.forEach((note, i) => addStroke(samples, Math.round(note.beat * period * sr), bassAt(i), level('bass')));

  for (let phrase = 0; phrase < phrases; phrase++) {
    const at = (c: number) => (phrase * PHRASE_BEATS + c - 1) * period;
    if (on('bell') && strokes) {
      for (const c of LAB_PARTS.bell) addStroke(samples, Math.round(at(c) * sr), strokes.open, level('bell'));
      for (const c of LAB_PARTS.bellNeck) addStroke(samples, Math.round(at(c) * sr), strokes.neck, level('bell'));
    } else if (fixtureBell) for (const c of LAB_PARTS.bell) place(samples, sr, at(c), level('bell'), fixtureBell);
    if (claveHit) for (const c of CLAVE[clave]) place(samples, sr, at(c), level('clave'), claveHit);
    if (fixtureBass) for (const c of LAB_PARTS.bass) place(samples, sr, at(c), level('bass'), c === Math.floor(c) ? fixtureBass.beat : fixtureBass.and);
    if (congaDrum) {
      for (const [stroke, counts] of CONGA_PLAN) for (const c of counts) addStroke(samples, Math.round(at(c) * sr), congaDrum[stroke], level('conga'));
    } else if (congaOn) {
      for (const c of LAB_PARTS.congaSlap) {
        // band-passed noise (a low-pass at 4 kHz minus one at 800 Hz), plus a short thump
        let lo = 0, hi = 0;
        const [aHi, aLo] = [4000, 800].map((fc) => 1 - Math.exp((-2 * Math.PI * fc) / sr)) as [number, number];
        burst(samples, sr, at(c), 0.09, level('conga'), attack((τ, j) => {
          const x = rnd(j + phrase * 131 + Math.round(c * 7));
          hi += aHi * (x - hi);
          lo += aLo * (x - lo);
          return 2.5 * (hi - lo) + 0.4 * tone(330)(τ);
        }), true);
      }
      for (const c of LAB_PARTS.congaOpen) place(samples, sr, at(c), level('conga'), c === Math.floor(c) ? congaOn.beat : congaOn.and);
    }
  }

  let peak = 0;
  for (const v of samples) peak = Math.max(peak, Math.abs(v));
  if (peak > 0.9) for (let i = 0; i < samples.length; i++) samples[i]! *= 0.9 / peak;
  return { sampleRate: sr, samples, beats, counts: beats.map((_, i) => (i % PHRASE_BEATS) + 1), loopSeconds: count * period };
}
